/**
 * audioConstants.ts — Shared constants for audio recording, voice activity detection,
 * and playback across the application.
 *
 * Consolidates magic values from RecorderService, VoiceActivityDetector, and follower
 * into a single source of truth.
 */

// {{{ audio filter frequencies (RecorderService)
/** High-pass filter cutoff frequency in Hz — removes low-frequency rumble below this threshold */
export const HIGHPASS_CUTOFF_HZ = 50

/** Low-pass filter cutoff frequency in Hz — removes high-frequency noise above this threshold */
export const LOWPASS_CUTOFF_HZ = 3000
// }}}

// {{{ RecorderService fallback defaults (when VAD is disabled)
/** Default sound level value (0–1 scale) used when VAD is disabled */
export const FAKE_SOUND_LEVEL = 0.5

/** Delay in ms before firing the fake onBaseLevel callback when VAD is disabled */
export const FAKE_BASE_LEVEL_DELAY_MS = 3000

/** Interval in ms for firing fake onSoundUpdate callbacks when VAD is disabled */
export const FAKE_SOUND_UPDATE_INTERVAL_MS = 100

/** Percentage value passed with the fake base level callback (100 = complete) */
export const FAKE_BASE_LEVEL_PERCENT = 100
// }}}

// {{{ Voice Activity Detector sampling
/** Interval in ms between VAD frequency analysis samples */
export const VAD_ANALYSIS_SAMPLE_PERIOD_MS = 50
// }}}

// {{{ sensitivity mapping (VoiceActivityDetector.setSensitivity)
/**
 * Minimum noise-multiplier-to-trigger value for the VAD sensitivity mapping.
 * At maximum sensitivity (100), the multiplier is this low — very easy to trigger.
 */
export const SENSITIVITY_MULTIPLIER_MIN = 1.0

/**
 * Maximum noise-multiplier-to-trigger value for the VAD sensitivity mapping.
 * At minimum sensitivity (1), the multiplier is this high — hard to trigger.
 */
export const SENSITIVITY_MULTIPLIER_MAX = 2.2
// }}}

// {{{ follower short-word sensitivity
/** Character-length threshold — words this short or shorter get extra VAD sensitivity */
export const SHORT_WORD_EXTRA_SENSITIVITY_LENGTH = 5

/** EWMA half-life multiplier for short words — lower = more responsive to silence */
export const SHORT_WORD_SENSITIVITY_MULTIPLIER = 0.5
// }}}

// {{{ VoiceActivityDetector tuning defaults
/** FFT buffer length for the VAD analyser node */
export const VAD_BUFFER_LEN = 4096

/**
 * Flip resistance (ms) — minimum time between voice state transitions.
 * Must match backend's VOICED_SEGMENT_GAP_MS (bmt/phrase_matching.py)
 * so the frontend detects phrase boundaries at the same threshold.
 */
export const VAD_FLIP_RESISTANCE_MS = 500

/** Duration (ms) to sample background noise before voice detection begins */
export const VAD_INITIAL_NOISE_CAPTURE_MS = 5500

/** Maximum frequency (Hz) for VAD frequency analysis */
export const VAD_MAX_CAPTURE_FREQ_HZ = 800

/** Minimum frequency (Hz) for VAD frequency analysis (covers low male range) */
export const VAD_MIN_CAPTURE_FREQ_HZ = 50

/** Minimum noise floor level — noise EWMA is clamped to at least this value */
export const VAD_MIN_NOISE_LEVEL = 0.15

/** EWMA half-life (ms) for the noise background estimator */
export const VAD_NOISE_EWMA_HALF_LIFE_MS = 1500

/** Factor by which EWMA must exceed noise to trigger voice-active state */
export const VAD_NOISE_MULTIPLIER_TO_TRIGGER = 1.16

/** Starting noise level for the noise EWMA before real data arrives */
export const VAD_NOISE_STARTING_LEVEL = 0.5

/** EWMA half-life (ms) for the short (fast) sound trend */
export const VAD_SHORT_EWMA_HALF_LIFE_MS = 50

/** EWMA half-life (ms) for the main sound trend (voice detection) */
export const VAD_SOUND_EWMA_HALF_LIFE_MS = 250

/** Fraction of sound-to-trigger descent that triggers EWMA slowdown for slow silence detection */
export const VAD_DESCENT_PERCENTAGE_TRIGGER = 0.1

/** Multiplier applied to soundEwmaHalfLife when slow silence detection slows down the EWMA */
export const VAD_SLOWDOWN_EWMA_MULTIPLIER = 2.0

/** AnalyserNode smoothing time constant (0–1, higher = smoother) */
export const VAD_SMOOTHING_TIME_CONSTANT = 0.2
// }}}
